import React, { useCallback, useContext, useMemo, useState, useRef, useEffect } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { StyledInputLabel } from './Input.component';
import { SelectInstance, StylesConfig, Theme, ThemeConfig } from 'react-select';
import { useTranslation } from 'react-i18next';
import { AsyncPaginate } from 'react-select-async-paginate';
import { uniqueId } from 'lodash';
import Switch from '@mui/material/Switch';

export interface SelectGroupedOption {
  readonly label: string;
  readonly options: readonly SelectOptionProps[];
}

export interface SelectOptionProps {
  value: string | number;
  code?: string;
  label: React.ReactNode;
  available?: boolean;
  isSelected?: boolean;
}

export interface SelectProps {
  label?: React.ReactNode;
  placeholder?: string;
  tooltip?: string;
  isMulti?: boolean;
  isSearchable?: boolean;
  disabled?: boolean;
  value?: string | number | SelectOptionProps | SelectOptionProps[];
  options?: SelectOptionProps[];
  initOptions?: SelectOptionProps[];
  defaultOptions?: SelectOptionProps[];
  loadOptions?: Function;
  isOptionDisabled?: boolean;
  onChangeOption?: Function;
  formatGroupLabel?:
    | ((group: SelectGroupedOption) => React.ReactNode)
    | undefined;
  resetStart?: boolean;
  onClose?: Function;
  additional?: object;
  isClearable?: boolean;
  menuPlacement?: 'auto' | 'top' | 'bottom';
  useMenuPortalTarget?: boolean;
  valueContainerMaxWidth?: number | string;
  blink?: boolean;
  // Ограничить максимальное количество выбранных элементов
  selectedItemsLimit?: number;
  selectGroup?: boolean;
  validationFailureMessage?: string;
  isSwitchable?: boolean;
  switchValue?: boolean;
  onSwitchChange?: (checked: boolean) => void;
  leftSwitchLabel? : string;
  rightSwitchLabel? : string;
}

const StyledSelect: StylesConfig = {
  control: (
    provided,
    // @ts-ignore
    { selectProps: { _theme, isValid, valueContainerMaxWidth, blink } },
  ) => ({
    ...provided,
    minHeight: '32px',
    background: _theme.colors.BG,
    borderColor: isValid ? _theme.colors.BORDER : _theme.colors.RED.R400,
    transition: 'outline-color .2s ease-in .1s',
    outline: 'transparent solid 1px',
    boxShadow: 'none',
    ...(valueContainerMaxWidth && {maxWidth: valueContainerMaxWidth}),
    ...(blink && {outline: `${_theme.colors.ORANGE.O400} solid 1px`}),
    '&:hover': {
      borderColor: _theme.colors.BLUE.B400,
    },
    '&:hover, &:active, &:focus-within': {
      outlineColor: 'transparent',
      transitionDelay: 'unset',
      transitionDuration: 'unset',
    },
    '&:focus-within': {
      borderColor: isValid ? _theme.colors.BLUE.B400 : _theme.colors.RED.R400
    }
  }),
  menu: provided => ({
    ...provided,
    overflow: 'hidden',
    background: 'rgba(255, 255, 255, 0.85)',
    backdropFilter: 'blur(10px)',
    padding: '0.25rem 0',
    boxShadow: '0 0.25rem 0.5rem 0.125rem rgba(114, 114, 114, 0.25)',
  }),
  clearIndicator: provided => ({
    ...provided,
    padding: '5px 8px',
  }),
  dropdownIndicator: provided => ({
    ...provided,
    padding: '5px 8px',
  }),
  menuPortal: provided => ({ ...provided, zIndex: 999999999 }),
  option: (
    provided,
    {
      isSelected,
      isFocused,
      // @ts-ignore
      selectProps: { _theme },
    },
  ) => ({
    ...provided,
    background: isSelected
      ? _theme.colors.BLUE.B400
      : isFocused
      ? 'rgba(0,0,0,0.06)'
      : 'transparent',
    width: 'auto',
    padding: '4px 4px 4px 8px',
    margin: '2px 4px',
    borderRadius: '4px',
    lineHeight: '1.5rem',
  }),
};

const StyledSelectTheme: ThemeConfig = (theme: Theme) => ({
  ...theme,
  borderRadius: 8,
});

export const StyledSelectErrorMessage = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.RED.R400};
`;

const StyledSelectWrapperDisabled = css`
  opacity: 0.7;

  &,
  & > * {
    cursor: default;
  }
`;
const StyledGroupLabel = styled.div`
  opacity: 0.7;
  cursor: pointer;
  display: block;
  color: hsl(0, 0%, 10%);
  font-size: 100%;
  font-weight: 800;
  margin-bottom: 0.25em;
  padding: 0 12px 0 12px;
  text-transform: uppercase;
  text-decoration: underline;
  box-sizing: border-box;
`;

export const StyledSelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 200px;
`;

export const StyledSelectWrapper = styled.div<{
  isDisabled: boolean;
}>`
  ${props => props.isDisabled && StyledSelectWrapperDisabled};
`;

export const SelectMenuPortalTargetContext = React.createContext<
  () => HTMLElement | null
>(() => document.body);

const SelectComponent = ({
  label,
  isMulti = false,
  isSearchable = true,
  placeholder,
  tooltip,
  value,
  disabled = false,
  isOptionDisabled = false,
  selectedItemsLimit,
  loadOptions: _loadOptions,
  options,
  initOptions,
  defaultOptions,
  onClose,
  onChangeOption,
  additional = {},
  isClearable = true,
  resetStart = false,
  formatGroupLabel,
  menuPlacement = 'auto',
  useMenuPortalTarget = false,
  valueContainerMaxWidth,
  blink,
  selectGroup,
  validationFailureMessage,
  isSwitchable = false,
  switchValue = false,
  onSwitchChange,
  leftSwitchLabel,
  rightSwitchLabel
}: SelectProps) => {
  const theme = useTheme() as any;
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const selectContainerRef = useRef<HTMLDivElement | null>(null);
  const inputElement = useRef<SelectInstance<unknown, boolean, SelectGroupedOption>>(null);
  const [ inputId, setInputId ] = useState('');
  const [isTooltipShown, setIsTooltipShown] = useState<boolean>(false);

  const getMenuPortalTarget = useContext(SelectMenuPortalTargetContext);
  const menuPortalTarget = useMemo(
    () => getMenuPortalTarget(),
    [getMenuPortalTarget],
  );

  const showTooltip = () => {
    setIsTooltipShown(true);
  }

  const hideTooltip = () => {
    setIsTooltipShown(false);
  }

  useEffect(() => {
    const selectInstance = inputElement.current;
    if (selectInstance !== undefined && selectInstance !== null) {
      const inputId = selectInstance.getElementId('input');
      setInputId(inputId);
    }
  }, []);

  useEffect(() => {
    const selectContainerElement = selectContainerRef.current;

    if (selectContainerElement === null || !tooltip) {
      return;
    }

    selectContainerElement.addEventListener("mouseenter", showTooltip);
    selectContainerElement.addEventListener("mouseleave", hideTooltip);

    return () => {
      selectContainerElement.removeEventListener("mouseenter", showTooltip);
      selectContainerElement.removeEventListener("mouseleave", hideTooltip);
    }
  }, [tooltip]);

  const loadOptions = (
    search: string,
    loadedOptions: string | any[],
    additional: any,
  ) => {
    if (resetStart && search.length < 3) return { options: [] };
    if (!_loadOptions) return { options: [] };
    return _loadOptions(search, loadedOptions, additional);
  };

  const handleClose = () => {
    setSearch(uniqueId(''));
    onClose && onClose();
  };

  const handleChange = (newValue: any) => {
    let value = newValue

    if (typeof selectedItemsLimit === 'number' && Array.isArray(value)) {
      value = value.slice(0, selectedItemsLimit)
    }

    onChangeOption && onChangeOption(value);
  };
  const handleSwitchChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    onSwitchChange && onSwitchChange(checked);
  };

  const groupHeadingIterator = useCallback(
    (eventName: string, iteratorCallback: Function) =>
      (e: React.MouseEvent) => {
        const options = (e.target as Element).parentNode?.lastChild?.childNodes;
        const event = new Event(eventName, { bubbles: true });
        selectGroup && Array.prototype.forEach.call(options, iteratorCallback(event));
      },
    [selectGroup],
  );

  return (
    <StyledSelectContainer>
      {label && (
        <StyledInputLabel>
          <label htmlFor={`${inputId}`}>{label}</label>
          {isSwitchable && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {leftSwitchLabel && <span>{leftSwitchLabel}</span>}
              <Switch checked={switchValue} onChange={handleSwitchChange} />
              {rightSwitchLabel && <span>{rightSwitchLabel}</span>}
            </div>
          )}
        </StyledInputLabel>
      )}
      <StyledSelectWrapper isDisabled={disabled} ref={selectContainerRef}>
        <AsyncPaginate
          selectRef={inputElement}
          value={value}
          theme={StyledSelectTheme}
          components={{
            LoadingIndicator: () => null,
            GroupHeading: props => {
              const { data, id } = props;
              return (
                <StyledGroupLabel
                  id={id}
                  onClick={groupHeadingIterator(
                    'click',
                    (event: Event) => (child: HTMLElement, ind: number) => {
                      setTimeout(() => child?.dispatchEvent(event), 50 * ind);
                    },
                  )}
                  onMouseOver={groupHeadingIterator(
                    'mouseover',
                    (event: Event) => (child: HTMLElement, ind: number) => {
                      if (ind === 0) child?.dispatchEvent(event);
                      child.style.backgroundColor = 'rgba(0,0,0,0.06)';
                    },
                  )}
                  onMouseOut={groupHeadingIterator(
                    'mouseover',
                    () => (child: HTMLElement) => {
                      child.style.removeProperty('background-color');
                    },
                  )}
                >
                  {data.label}
                </StyledGroupLabel>
              );
            },
          }}
          _theme={theme}
          // @ts-ignore
          styles={StyledSelect}
          valueContainerMaxWidth={valueContainerMaxWidth}
          blink={blink && !value}
          placeholder={placeholder && t(placeholder)}
          menuPortalTarget={useMenuPortalTarget ? menuPortalTarget : null}
          backspaceRemovesValue={true}
          captureMenuScroll={false}
          formatGroupLabel={formatGroupLabel}
          isMulti={isMulti}
          isSearchable={isSearchable}
          isValid={!validationFailureMessage}
          isDisabled={disabled}
          isClearable={isClearable}
          options={[...(initOptions || []), ...(options || [])]}
          defaultOptions={defaultOptions}
          // @ts-ignore
          loadOptions={loadOptions}
          // @ts-ignore
          onChange={handleChange}
          onMenuClose={handleClose}
          menuPlacement={menuPlacement}
          menuShouldBlockScroll={true}
          escapeClearsValue={true}
          debounceTimeout={300}
          // @ts-ignore
          cacheUniqs={[...(initOptions || []), ...(options || [])] || [search]}
          isOptionDisabled={() => isOptionDisabled}
          additional={{ page: 1, ...additional }}
          onInputChange={(newValue: string) => setSearch(newValue)}
          closeMenuOnSelect={!isMulti}
        />

        {validationFailureMessage && (
          <StyledSelectErrorMessage>
            {validationFailureMessage}
          </StyledSelectErrorMessage>
        )}

        {isTooltipShown && <div>{tooltip}</div>}
      </StyledSelectWrapper>
    </StyledSelectContainer>
  );
};

export default SelectComponent;
